// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { formatHumanName, type MedplumClient } from '@medplum/core';
import type {
  Appointment,
  Coding,
  Coverage,
  Encounter,
  Patient,
  Questionnaire,
  QuestionnaireItem,
  QuestionnaireResponse,
  QuestionnaireResponseItem,
  QuestionnaireResponseItemAnswer,
} from '@medplum/fhirtypes';
import referenceTemplate from '../assets/patient-self-intake-reference.html?raw';
import polarisLogo from '../assets/polaris-logo.svg?raw';
import { NOTE_CODES } from '../components/encounter/encounter-chart-types';

const NOTE_OBSERVATION_SYSTEM = 'https://polarissleep.com/fhir/observation-type';
const EXCLUDED_QUESTIONNAIRE_NAMES = new Set(['pcp-referral-provider-info']);

export interface PatientEncounterNote {
  encounterId: string;
  title: string;
  assessment: string;
  noteToPatient: string;
  dateAdded?: string;
}

export interface ScreenerExportEntry {
  questionnaire: Questionnaire;
  latestQR: QuestionnaireResponse;
}

export interface PatientSelfIntakeHtmlInput {
  patient: Patient;
  entries: ScreenerExportEntry[];
  coverages?: Coverage[];
}

function getEncounterTitle(encounter: Encounter | undefined): string {
  return (
    encounter?.type?.[0]?.text ??
    encounter?.type?.[0]?.coding?.[0]?.display ??
    encounter?.reasonCode?.[0]?.text ??
    'Encounter Note'
  );
}

function toTimestamp(value: string | undefined): number {
  if (!value) {
    return 0;
  }

  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

export async function fetchPatientEncounterNotes(
  medplum: MedplumClient,
  patientId: string
): Promise<PatientEncounterNote[]> {
  const [activeEncounters, cancelledEncounters, observations] = await Promise.all([
    medplum.searchResources('Encounter', {
      subject: `Patient/${patientId}`,
      'status:not': 'cancelled',
      _sort: '-_lastUpdated',
      _count: '200',
    }),
    medplum.searchResources('Encounter', {
      subject: `Patient/${patientId}`,
      status: 'cancelled',
      _sort: '-_lastUpdated',
      _count: '200',
    }),
    medplum.searchResources(
      'Observation',
      {
        subject: `Patient/${patientId}`,
        status: 'final',
        code: NOTE_CODES.map((code) => `${NOTE_OBSERVATION_SYSTEM}|${code}`).join(','),
        _sort: '-_lastUpdated',
        _count: '200',
      },
      { cache: 'no-cache' }
    ),
  ]);

  const noteByEncounterId = new Map<
    string,
    {
      assessment: string;
      noteToPatient: string;
      dateAdded?: string;
      timestamp: number;
    }
  >();

  for (const observation of observations) {
    const encounterId = observation.encounter?.reference?.split('/')[1];
    if (!encounterId) {
      continue;
    }

    const code = observation.code?.coding?.find((coding: Coding) => coding.system === NOTE_OBSERVATION_SYSTEM)?.code;
    const updatedAt = observation.meta?.lastUpdated ?? observation.issued;
    const nextTimestamp = toTimestamp(updatedAt);
    const current = noteByEncounterId.get(encounterId) ?? {
      assessment: '',
      noteToPatient: '',
      dateAdded: updatedAt,
      timestamp: nextTimestamp,
    };

    if (code === 'assessment' && !current.assessment) {
      current.assessment = observation.valueString ?? '';
    }

    if (code === 'patient-note' && !current.noteToPatient) {
      current.noteToPatient = observation.valueString ?? '';
    }

    if (nextTimestamp > current.timestamp) {
      current.dateAdded = updatedAt;
      current.timestamp = nextTimestamp;
    }

    noteByEncounterId.set(encounterId, current);
  }

  const noShowEncounters = (
    await Promise.all(
      (cancelledEncounters as Encounter[]).map(async (encounter) => {
        const appointmentReference = encounter.appointment?.find((reference) => reference.reference)?.reference;
        if (!appointmentReference) {
          return undefined;
        }

        const appointment = await medplum
          .readReference<Appointment>({ reference: appointmentReference })
          .catch(() => null);
        return appointment?.status === 'noshow' ? encounter : undefined;
      })
    )
  ).filter((encounter): encounter is Encounter => Boolean(encounter));

  return [...(activeEncounters as Encounter[]), ...noShowEncounters]
    .filter((encounter) => Boolean(encounter.id))
    .map((encounter) => {
      const note = noteByEncounterId.get(encounter.id ?? '');
      const fallbackDate = encounter.meta?.lastUpdated ?? encounter.period?.start ?? encounter.period?.end;

      return {
        encounterId: encounter.id ?? '',
        title: getEncounterTitle(encounter),
        assessment: note?.assessment ?? '',
        noteToPatient: note?.noteToPatient ?? '',
        dateAdded: note?.dateAdded ?? fallbackDate,
      } satisfies PatientEncounterNote;
    })
    .filter((note) => Boolean(note.encounterId))
    .sort((a, b) => toTimestamp(b.dateAdded) - toTimestamp(a.dateAdded));
}

export async function fetchPatientScreenerEntries(
  medplum: MedplumClient,
  patientId: string
): Promise<ScreenerExportEntry[]> {
  const allQRs = await medplum.searchResources('QuestionnaireResponse', {
    subject: `Patient/${patientId}`,
    _sort: '-authored',
    _count: '200',
  });

  const latestByQuestionnaire = new Map<string, QuestionnaireResponse>();
  for (const qr of allQRs) {
    if (!qr.questionnaire) {
      continue;
    }
    if (!latestByQuestionnaire.has(qr.questionnaire)) {
      latestByQuestionnaire.set(qr.questionnaire, qr);
    }
  }

  const results = await Promise.all(
    Array.from(latestByQuestionnaire.entries()).map(async ([questionnaireRef, latestQR]) => {
      const id = questionnaireRef.replace('Questionnaire/', '');
      const questionnaire = await medplum.readResource('Questionnaire', id).catch(() => null);
      if (!questionnaire) {
        return null;
      }
      if (EXCLUDED_QUESTIONNAIRE_NAMES.has(questionnaire.name ?? '')) {
        return null;
      }
      return { questionnaire, latestQR } satisfies ScreenerExportEntry;
    })
  );

  return (results.filter((entry) => entry !== null) as ScreenerExportEntry[]).sort((a, b) =>
    (a.latestQR.authored ?? '').localeCompare(b.latestQR.authored ?? '')
  );
}

function toCsvCell(value: string | undefined): string {
  const normalized = value ?? '';
  return `"${normalized.replaceAll('"', '""')}"`;
}

export function buildEncounterNotesCsv(notes: PatientEncounterNote[]): string {
  const rows = [
    ['Title', 'Assessment', 'Note to Patient', 'Date Added', 'Encounter ID'].join(','),
    ...notes.map((note) =>
      [
        toCsvCell(note.title),
        toCsvCell(note.assessment),
        toCsvCell(note.noteToPatient),
        toCsvCell(note.dateAdded),
        toCsvCell(note.encounterId),
      ].join(',')
    ),
  ];

  return rows.join('\n');
}

function buildQuestionTextMap(
  items: QuestionnaireItem[] | undefined,
  out = new Map<string, string>()
): Map<string, string> {
  for (const item of items ?? []) {
    if (item.linkId && item.text) {
      out.set(item.linkId, item.text);
    }
    buildQuestionTextMap(item.item, out);
  }
  return out;
}

function formatAnswer(answer: QuestionnaireResponseItemAnswer): string {
  if (answer.valueBoolean !== undefined) {
    return answer.valueBoolean ? 'Yes' : 'No';
  }
  if (answer.valueInteger !== undefined) {
    return String(answer.valueInteger);
  }
  if (answer.valueDecimal !== undefined) {
    return String(answer.valueDecimal);
  }
  if (answer.valueString !== undefined) {
    return answer.valueString;
  }
  if (answer.valueDate !== undefined) {
    return answer.valueDate;
  }
  if (answer.valueDateTime !== undefined) {
    return answer.valueDateTime;
  }
  if (answer.valueTime !== undefined) {
    return answer.valueTime;
  }
  if (answer.valueCoding) {
    return answer.valueCoding.display ?? answer.valueCoding.code ?? '';
  }
  if (answer.valueAttachment) {
    return answer.valueAttachment.title ?? answer.valueAttachment.url ?? 'Attachment';
  }
  return '';
}

export function buildPatientSelfIntakeHtml({ patient, entries, coverages = [] }: PatientSelfIntakeHtmlInput): string {
  const patientName = patient.name?.[0] ? formatHumanName(patient.name[0]) : 'Unnamed patient';
  const address = patient.address?.[0];
  const dateText = (value: string | undefined): string => (value ? new Date(value).toLocaleString() : 'Unknown');
  const answerMap = new Map<string, string>();
  const answerRows: [string, string][] = [];
  const walk = (items: QuestionnaireResponseItem[] | undefined, textByLinkId: Map<string, string>): void => {
    for (const item of items ?? []) {
      const question = textByLinkId.get(item.linkId ?? '') ?? item.text ?? item.linkId ?? 'Question';
      const answers = item.answer?.map(formatAnswer).filter(Boolean) ?? [];
      if (answers.length > 0) {
        const answer = answers.join(', ');
        answerRows.push([question, answer]);
        if (item.linkId) {
          answerMap.set(item.linkId, answer);
        }
      }
      walk(item.item, textByLinkId);
    }
  };
  const completedAt = entries
    .map(({ latestQR }) => latestQR.authored ?? latestQR.meta?.lastUpdated)
    .filter((value): value is string => Boolean(value))
    .sort()
    .at(-1);
  for (const { questionnaire, latestQR } of entries) {
    walk(latestQR.item, buildQuestionTextMap(questionnaire.item));
  }

  const stopBangEntry = entries.find(({ questionnaire }) => (questionnaire.name ?? '').includes('stop-bang'));
  const epworthEntry = entries.find(({ questionnaire }) => (questionnaire.name ?? '').includes('epworth'));
  const rowsForEntry = (entry: ScreenerExportEntry | undefined): [string, string, number][] => {
    if (!entry) {
      return [];
    }
    const textByLinkId = buildQuestionTextMap(entry.questionnaire.item);
    const rows: [string, string, number][] = [];
    const collect = (items: QuestionnaireResponseItem[] | undefined): void => {
      for (const item of items ?? []) {
        const answer = item.answer?.map(formatAnswer).filter(Boolean) ?? [];
        if (answer.length > 0) {
          rows.push([
            textByLinkId.get(item.linkId ?? '') ?? item.text ?? 'Question',
            answer.join(', '),
            Number(answer[0]) || 0,
          ]);
        }
        collect(item.item);
      }
    };
    collect(entry.latestQR.item);
    return rows;
  };
  const stopBangRows = rowsForEntry(stopBangEntry).map(
    ([question, answer]) => [question, answer, /^yes\b/i.test(answer) ? 1 : 0] as [string, string, number]
  );
  const epworthRows = rowsForEntry(epworthEntry);
  const addressText = [
    ...(address?.line ?? []),
    [address?.city, address?.state, address?.postalCode].filter(Boolean).join(', '),
  ]
    .filter(Boolean)
    .join(', ');
  const coverage = coverages[0];
  const patientData = {
    name: patientName,
    dob: patient.birthDate ? `${patient.birthDate}` : '',
    sex: patient.gender ?? '',
    pronouns: '',
    language: answerMap.get('primary-language') ?? '',
    phone: patient.telecom?.find((t) => t.system === 'phone')?.value ?? '',
    email: patient.telecom?.find((t) => t.system === 'email')?.value ?? '',
    contact: '',
    address: addressText,
  };
  const questionnaireData = {
    meta: {
      submitted: dateText(completedAt),
      intakeId: patient.id ?? 'Unknown',
      path: 'Completed self-intake questionnaire',
    },
    patient: patientData,
    coverage: {
      type: coverage?.type?.text ?? coverage?.type?.coding?.[0]?.display ?? '',
      carrier: coverage?.payor?.[0]?.display ?? '',
      memberId: coverage?.subscriberId ?? '',
      carrier2: '',
      memberId2: '',
      cost: '',
      eligibility: '',
    },
    reason: answerMap.get('reason-for-referral') ?? 'Sleep concern',
    eligibilityPath: 'Completed self-intake questionnaire',
    diagnosed: [],
    flagConditions: [],
    history: answerRows,
    epworth: epworthRows.map(([, , score]) => score),
    measure: null,
    stopbang: stopBangRows,
    consents: [],
    signature: { name: patientName, when: dateText(completedAt), attest: '' },
  };
  const appointmentData = {
    ...questionnaireData,
    meta: { ...questionnaireData.meta, path: 'Appointment path' },
    eligibilityPath: 'Scheduled appointment',
    history: [],
    epworth: [],
    stopbang: [],
  };
  const safeJson = (value: unknown) =>
    JSON.stringify(value).replace(
      /[<>&\u2028\u2029]/g,
      (character) =>
        ({ '<': '\\u003c', '>': '\\u003e', '&': '\\u0026', '\u2028': '\\u2028', '\u2029': '\\u2029' })[character] ??
        character
    );
  const dataScript = `const questionnaire = ${safeJson(questionnaireData)};\n    const appointment = ${safeJson(appointmentData)};`;
  return referenceTemplate
    .replace(
      '<div class="brand"><img src="assets/polaris-wordmark.png" alt="Polaris Sleep" /></div>',
      `<div class="brand">${polarisLogo}</div>`
    )
    .replace('.brand img{height:30px;display:block;}', '.brand img,.brand svg{height:30px;width:auto;display:block;}')
    .replace(/const questionnaire = \{[\s\S]*?\n {4}const has =/, `${dataScript}\n\n    const has =`)
    .replace(
      'const esc = s => String(s);',
      "const esc = s => String(s).replaceAll('&','&amp;').replaceAll('<','&lt;').replaceAll('>','&gt;').replaceAll('\\\"','&quot;');"
    )
    .replace('· June 12, 2026', `· ${dateText(completedAt).split(',')[0]}`);
}

function formatQuestionnaireItems(
  items: QuestionnaireResponseItem[] | undefined,
  questionTextByLinkId: Map<string, string>,
  depth = 0
): string[] {
  const lines: string[] = [];

  for (const item of items ?? []) {
    const question = questionTextByLinkId.get(item.linkId ?? '') ?? item.text ?? item.linkId ?? 'Question';
    const prefix = '  '.repeat(depth);
    const answers = item.answer?.map(formatAnswer).filter(Boolean) ?? [];

    if (answers.length > 0) {
      lines.push(`${prefix}${question}: ${answers.join('; ')}`);
    } else if (item.item?.length) {
      lines.push(`${prefix}${question}:`);
    }

    lines.push(...formatQuestionnaireItems(item.item, questionTextByLinkId, depth + 1));
  }

  return lines;
}

export function buildScreenersExportText(patient: Patient, entries: ScreenerExportEntry[]): string {
  const patientName = patient.name?.[0] ? formatHumanName(patient.name[0]) : 'Unnamed patient';
  const sections = [
    `Patient: ${patientName}`,
    `Patient ID: ${patient.id ?? 'Unknown'}`,
    '',
    ...entries.flatMap(({ questionnaire, latestQR }, index) => {
      const questionTextByLinkId = buildQuestionTextMap(questionnaire.item);
      const lines = formatQuestionnaireItems(latestQR.item, questionTextByLinkId);
      return [
        `${index + 1}. ${questionnaire.title ?? questionnaire.name ?? questionnaire.id ?? 'Questionnaire'}`,
        `Completed: ${latestQR.authored ?? latestQR.meta?.lastUpdated ?? 'Unknown'}`,
        ...(lines.length > 0 ? lines : ['No answers recorded.']),
        '',
      ];
    }),
  ];

  return sections.join('\n').trim();
}

export function slugifyPatientName(patient: Patient): string {
  const name = patient.name?.[0] ? formatHumanName(patient.name[0]) : (patient.id ?? 'patient');
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export function downloadBlobFile(filename: string, content: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  link.style.display = 'none';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
